import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import { Image } from './PartnersSelectImage'

import integrationPng from './assets/Integration.png'
import integrationPng2x from './assets/Integration@2x.png'
import integrationWebp from './assets/Integration.webp'
import integrationWebp2x from './assets/Integration@2x.webp'

import channelPng from './assets/Channel.png'
import channelPng2x from './assets/Channel@2x.png'
import channelWebp from './assets/Channel.webp'
import channelWebp2x from './assets/Channel@2x.webp'

const techCopy = {
  heading: `Technology Partners`,
  description: `Integrate your software with Segment. Grow your user base by joining the Twilio Segment Partner Program.`,
}

const techLink = {
  text: `Learn more`,
  url: `/partners/integration/`,
}

const channelCopy = {
  heading: `Channel Partners`,
  description: `Help customers implement Segment. Develop new revenue streams by empowering your clients through customer data.`,
}

const channelLink = {
  text: `Learn more`,
  url: `/partners/channel/`,
}

const PartnersSelect: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <SectionStyled variant="centered">
      <ColumnTrack columns={1} overrides={{ md: 2 }}>
        <Slot>
          <Image
            images={{
              png: integrationPng,
              png2x: integrationPng2x,
              webp: integrationWebp,
              webp2x: integrationWebp2x,
            }}
            alt="Technology Partners"
          />
          <CopyText.Heading variant="h2" tag="h2">
            {techCopy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pLarge">{techCopy.description}</CopyText.Description>
          <CopyText.Link href={getAbsoluteUrl(techLink.url, pageConfig)}>{techLink.text}</CopyText.Link>
        </Slot>
        <Slot>
          <Image
            images={{
              png: channelPng,
              png2x: channelPng2x,
              webp: channelWebp,
              webp2x: channelWebp2x,
            }}
            alt="Channel Partners"
          />
          <CopyText.Heading variant="h2" tag="h2">
            {channelCopy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pLarge">{channelCopy.description}</CopyText.Description>
          <CopyText.Link href={getAbsoluteUrl(channelLink.url, pageConfig)}>{channelLink.text}</CopyText.Link>
        </Slot>
      </ColumnTrack>
    </SectionStyled>
  )
}

const Slot = styled.div``

const SectionStyled = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-bottom: 0;
    padding-top: 0;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      padding: 80px 120px 0;
    }
  }
`

export default PartnersSelect
